/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './MercurXContractDetailInputs.scss';
import { Button, Col, Form, Row, Card, Dropdown } from 'react-bootstrap';
import Web3 from 'web3';
import { ethers } from 'ethers';
import Swal from 'sweetalert2';
import { mainColors } from '../../helpers/colors';
import wallet from '../../helpers/wallet';
import { abiRequestAction } from '../../store/abi/abiActions';
import { getBNB } from '../../store/bnb/bnbActions';
import { setAlertAction } from '../../store/alert/alertActions';
import { setLoadingFullAction } from '../../store/loading/loadingActions';
import { transactionRequest } from '../../store/bo_transaction/bo_transactionActions';
import { useLocation } from 'react-router-dom';
import MercurXIcons from '../../components/MercurXIcons/MercurXIcons';
import { getContractExecutionErrorMessage } from '../../helpers/contractExecutionError';

const MercurXContractDetailInputs = ({ ...props }) => {
  const location = useLocation();
  const { itemName, item } = location.state || {};
  const [selectedUnit, setSelectedUnit] = useState('WEI');
  const [contractFunctions, setContractFunctions] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState('');
  const [functionParams, setFunctionParams] = useState([]);
  const [paramValues, setParamValues] = useState([]);
  const [results, setResults] = useState(null);
  const [resultsInput, setResultsInput] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [validationAddressError, setValidationAddressError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const validateParams = () => {
    const ethAddressRegex = /^0x[a-fA-F0-9]{40}$/;
    for (let i = 0; i < paramValues.length; i++) {
      const paramName = contractFunctions[i]?.name;
      const value = paramValues[i];

      if ((paramName === 'address' || paramName === 'spender'
        || paramName === 'owner' || paramName === 'account'
        || paramName === 'from' || paramName === 'to') && !ethAddressRegex.test(value)) {
        setValidationAddressError(`The input for ${paramName} must be a valid Ethereum address.`);
        return false;
      }
    }
    setValidationAddressError(null);
    return true;
  };

  const handleUnitChange = (unit) => setSelectedUnit(unit);

  const {
    projects,
    abiHistoryRequest,
    abiHistory,
    getBNBRequest,
    bnbData,
    setAlert,
    setLoadingFull,
    transactionRequest,
    isLoading,
    accounts
  } = props;

  const getFunctionSignature = (fn) => {
    if (!fn?.name) {
      return '';
    }

    const types = (fn.inputs || []).map((input) => input?.type || '').join(',');
    return `${fn.name}(${types})`;
  };

  const getSelectedAbi = () => {
    const abiFunctions = abiHistory?.[itemName]?.data || [];
    return abiFunctions.find((fn) => getFunctionSignature(fn) === selectedFunction);
  };

  const getFunctionLabel = (fn) => {
    const inputs = (fn?.inputs || []).map((input, index) => {
      const paramName = input?.name || `arg${index + 1}`;
      return `${paramName}: ${input?.type || 'unknown'}`;
    }).join(', ');

    return `${fn?.name || ''}(${inputs})`;
  };

  const getFunctionShortLabel = (fn) => {
    if (!fn?.name) {
      return 'Select a function';
    }

    const inputLength = (fn?.inputs || []).length;
    if (!inputLength) {
      return `${fn.name}()`;
    }

    return `${fn.name} (${inputLength} parameter${inputLength > 1 ? 's' : ''})`;
  };

  const isReadFunction = (fn) => ['view', 'pure'].includes(fn?.stateMutability);

  const getGroupedAbiFunctions = () => {
    const abiFunctions = abiHistory?.[itemName]?.data || [];

    return {
      writeFunctions: abiFunctions.filter((fn) => !isReadFunction(fn)),
      readFunctions: abiFunctions.filter((fn) => isReadFunction(fn)),
    };
  };

  const normalizeContractResult = (value) => {
    if (typeof value === 'bigint') {
      return value.toString();
    }

    if (Array.isArray(value)) {
      return value.map((item) => normalizeContractResult(item));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce((acc, [key, item]) => {
        acc[key] = normalizeContractResult(item);
        return acc;
      }, {});
    }

    return value;
  };

  const formatContractResult = (value) => {
    const normalizedValue = normalizeContractResult(value);

    if (typeof normalizedValue === 'string') {
      return normalizedValue;
    }

    return JSON.stringify(normalizedValue, null, 2);
  };

  const getResultCardMeta = (value) => {
    const normalizedValue = normalizeContractResult(value);

    if (
      normalizedValue &&
      typeof normalizedValue === 'object' &&
      !Array.isArray(normalizedValue) &&
      typeof normalizedValue.error === 'string'
    ) {
      return {
        variant: 'error',
        title: 'Request Error',
        content: normalizedValue.error,
        isCodeBlock: false,
      };
    }

    if (
      typeof normalizedValue === 'string' ||
      typeof normalizedValue === 'number' ||
      typeof normalizedValue === 'boolean'
    ) {
      return {
        variant: 'success',
        title: 'Response Value',
        content: String(normalizedValue),
        isCodeBlock: false,
      };
    }

    return {
      variant: 'info',
      title: 'Response Payload',
      content: formatContractResult(normalizedValue),
      isCodeBlock: true,
    };
  };

  const renderResultCard = (sectionLabel, value) => {
    const resultMeta = getResultCardMeta(value);

    return (
      <Card className={`mt-4 contract-result-card contract-result-card--${resultMeta.variant}`}>
        <Card.Header className="contract-result-header">
          <div>
            <div className="contract-result-section-label">{sectionLabel}</div>
            <div className="contract-result-title">{resultMeta.title}</div>
          </div>
          <div className={`contract-result-badge contract-result-badge--${resultMeta.variant}`}>
            {resultMeta.variant === 'error' ? 'Error' : 'Result'}
          </div>
        </Card.Header>
        <Card.Body className="contract-result-body">
          {resultMeta.isCodeBlock ? (
            <pre className="contract-result-code">{resultMeta.content}</pre>
          ) : (
            <div className={`contract-result-message contract-result-message--${resultMeta.variant}`}>
              {resultMeta.content}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const copyTextFallback = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'absolute';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    const isCopied = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (!isCopied) {
      throw new Error('Copy command failed.');
    }
  };

  const handleCopyContractAddress = async () => {
    if (!hasResolvedContractAddress) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(resolvedContractAddress);
      } else {
        copyTextFallback(resolvedContractAddress);
      }

      setAlert({
        title: 'Success!',
        text: 'Contract address copied to clipboard.',
        variant: 'success',
        outTimeMS: 2500,
      });
    } catch (error) {
      console.error('Unable to copy contract address:', error);
      setAlert({
        title: 'Error!',
        text: 'Contract address could not be copied.',
        variant: 'danger',
        outTimeMS: 2500,
      });
    }
  };

  const getMercurxTokenProject = () => {
    if (!Array.isArray(projects) || !projects.length) {
      return null;
    }

    const tokenProjects = projects.filter((projectItem) => projectItem?.token?.address);
    if (!tokenProjects.length) {
      return null;
    }

    const prioritizedProject = tokenProjects.find((projectItem) => {
      const projectName = (projectItem?.name || '').toUpperCase();
      const tokenName = (projectItem?.token?.name || '').toUpperCase();
      const tokenSymbol = (projectItem?.token?.symbol || '').toUpperCase();

      return (
        projectName.includes('MERCURX') ||
        tokenName.includes('MERCURX') ||
        tokenSymbol === 'MERX'
      );
    });

    return prioritizedProject || tokenProjects[0];
  };

  const isValidEthAddress = (value) => /^0x[a-fA-F0-9]{40}$/.test((value || '').trim());

  const formatProjectNameFromItemName = () => {
    if (!itemName) {
      return 'CONTRACT';
    }

    return itemName
      .replace(/_?abi$/i, '')
      .replace(/_/g, ' ')
      .trim()
      .toUpperCase();
  };

  const getMetadataContractAddress = () => {
    const candidates = [
      item?.data?.contract_address,
      item?.data?.address,
      item?.data?.deployed_address,
      item?.data?.contractAddress,
      abiHistory?.[itemName]?.contract_address,
      abiHistory?.[itemName]?.address,
      abiHistory?.[itemName]?.deployed_address,
      abiHistory?.[itemName]?.contractAddress,
    ];

    return candidates.find((value) => isValidEthAddress(value)) || '';
  };

  const getDefaultContractAddress = () => {
    return getMetadataContractAddress();
  };

  const getExecutionContractAddress = () => {
    return getDefaultContractAddress();
  };

  const resolvedContractAddress = getExecutionContractAddress();
  const hasResolvedContractAddress = isValidEthAddress(resolvedContractAddress);

  const handleAbi = () => {
    abiHistoryRequest();
    getBNBRequest();
  };

  useEffect(() => {
    handleAbi();
  }, []);

  const runContract = async () => {
    if (!validateParams()) {
      return;
    }

    const selectedAbi = getSelectedAbi();
    if (!selectedAbi) {
      Swal.fire({
        icon: 'error',
        iconColor: mainColors.primary,
        text: 'The selected function could not be found. Please refresh the page and try again.',
        confirmButtonColor: mainColors.primary,
      });
      return;
    }

    const executionContractAddress = getExecutionContractAddress();
    if (!isValidEthAddress(executionContractAddress)) {
      Swal.fire({
        icon: 'error',
        iconColor: mainColors.primary,
        text: 'No saved contract address was found for this contract.',
        confirmButtonColor: mainColors.primary,
      });
      return;
    }

    const tokenProject = getMercurxTokenProject();
    const tokenAddress = tokenProject?.token?.address || executionContractAddress;

    let signerAddress = accounts?.[0] || '';
    let transactionDetails;
    setLoadingFull({ isLoading: true, loadingText: 'Waiting...' });
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = await provider.getSigner();
      signerAddress = await signer.getAddress();
      const web3 = new Web3(window.ethereum);
      const project_id = 1;
      const project_name = formatProjectNameFromItemName();
      const transaction_time = new Date();
      const user_public_address = signerAddress;
      const action = selectedAbi?.name || selectedFunction;
      const token_address = tokenAddress;
      await wallet.controlAndSwitchOrAddNetwork();
      await window.ethereum.enable();

      const mercurx_contract = new web3.eth.Contract(
        abiHistory?.[itemName]?.data,
        executionContractAddress
      );
      const contractMethod = mercurx_contract.methods[selectedFunction];
      if (!contractMethod) {
        throw new Error(`Function ${selectedFunction} not found on contract.`);
      }

      setResults(null);
      setResultsInput(null);

      if (selectedAbi.stateMutability === 'view') {
        const result = await contractMethod(...paramValues).call();
        if (contractFunctions.length === 0) {
          setResults(result);
          console.log('Call result:', result);
        } else {
          setResultsInput(result);
          console.log('Call input result:', result);
        }
      } else {
        const transaction = await contractMethod(...paramValues).send({
          from: signerAddress,
          to: executionContractAddress,
        });
        transactionDetails = transaction;
        setTransactions((prev) => [...prev, transaction]);
        const transaction_hash = transaction.transactionHash;
        const transaction_status = transaction.status;
        const token_count = paramValues.length > 1 ? paramValues[paramValues.length - 1] : 0;
        // TODO : 0lar null olacak.
        const payload2 = {
          action,
          project_id,
          project_name,
          token_count,
          user_public_address,
          token_address,
          transaction_hash,
          transaction_time,
          transaction_status,
        };
        console.log(payload2)
        transactionRequest(payload2);

        Swal.fire({
          icon: 'success',
          iconColor: mainColors.primary,
          text: 'Transaction succeeded',
          confirmButtonColor: mainColors.primary,
          html: `<a href=https://testnet.bscscan.com/tx/${transactionDetails?.transactionHash}
            target='_blank'> Check Detail Transaction !</a>`,
        });
      }
    } catch (err) {
      console.error('Error during contract execution:', err);
      const errorMessage = getContractExecutionErrorMessage(err);

      if (selectedAbi?.stateMutability === 'view') {
        if (contractFunctions.length === 0) {
          setResults({ error: errorMessage });
        } else {
          setResultsInput({ error: errorMessage });
        }
      }

      Swal.fire({
        icon: 'error',
        iconColor: mainColors.primary,
        title: 'Request failed',
        text: errorMessage,
        confirmButtonColor: mainColors.primary,
      });

      const transaction_hash = '';
      const transaction_status = false;
      const transaction_time = new Date();
      const project_id = 1;
      const project_name = formatProjectNameFromItemName();
      const token_count = paramValues.length > 1 ? paramValues[paramValues.length - 1] : 0;
      // TODO : 0lar null olacak.
      const user_public_address = signerAddress;
      const action = selectedAbi?.name || selectedFunction;
      const token_address = tokenAddress;
      const payload2 = {
        action,
        project_id,
        project_name,
        token_count,
        user_public_address,
        token_address,
        transaction_hash,
        transaction_time,
        transaction_status,
      };
      console.log(payload2)
      transactionRequest(payload2);
    } finally {
      setLoadingFull({ isLoading: false });
    }
  };

  const runContractOthers = async () => {
    if (!validateParams()) {
      return;
    }

    const tokenProject = getMercurxTokenProject();
    const tokenAddress = tokenProject?.token?.address;
    const selectedContractAddress = itemName === "MERX_presale_abi"
      ? tokenProject?.token?.presale_contract?.contract_address
      : tokenProject?.token?.staking_contract?.contract_address;

    if (!tokenAddress || !selectedContractAddress) {
      Swal.fire({
        icon: 'error',
        iconColor: mainColors.primary,
        text: 'The required contract address could not be found for this action.',
        confirmButtonColor: mainColors.primary,
      });
      return;
    }

    let signerAddress = accounts?.[0] || '';
    let transactionDetails;
    setLoadingFull({ isLoading: true, loadingText: 'Waiting...' });
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = await provider.getSigner();
      signerAddress = await signer.getAddress();
      const web3 = new Web3(window.ethereum);
      const project_id = 1;
      const project_name = itemName === "MERX_presale_abi" ? "MERCURX_PRESALE" : "MERCURX_STAKE";
      const transaction_time = new Date();
      const user_public_address = signerAddress;
      const selectedAbi = getSelectedAbi();
      const action = selectedAbi?.name || selectedFunction;
      const token_address = tokenAddress;

      await wallet.controlAndSwitchOrAddNetwork();
      await window.ethereum.enable();

      const mercurx_contract = new web3.eth.Contract(
        abiHistory?.[itemName]?.data,
        selectedContractAddress
      );
      const contractMethod = mercurx_contract.methods[selectedFunction];
      const data = contractMethod(...paramValues).encodeABI();
      const selectedContract = selectedAbi;

      setResults(null);
      setResultsInput(null);

      if (selectedContract.stateMutability === 'view') {
        const result = await contractMethod(...paramValues).call();
        const formattedResult = typeof result === 'boolean' ? result.toString() : result;
        if (contractFunctions.length === 0) {
          setResults(formattedResult);
          console.log('Call result:', formattedResult);
        } else {
          setResultsInput(formattedResult);
          console.log('Call input result:', formattedResult);
        }
      } else {
        const transaction = await contractMethod(...paramValues).send({
          from: signerAddress,
          to: selectedContractAddress,
          data: web3.eth.abi.encodeFunctionSignature('whitdrawETH()'),
        });

        transactionDetails = transaction;
        setTransactions((prev) => [...prev, transaction]);
        const transaction_hash = transaction.transactionHash;
        const transaction_status = transaction.status;
        const token_count = paramValues.length > 1 ? paramValues[paramValues.length - 1] : 0;
        // TODO : 0lar null olacak.

        const payload2 = {
          action,
          project_id,
          project_name,
          token_count,
          user_public_address,
          token_address,
          transaction_hash,
          transaction_time,
          transaction_status,
        };
        console.log(payload2)
        transactionRequest(payload2);
        Swal.fire({
          icon: 'success',
          iconColor: mainColors.primary,
          text: 'Transaction succeeded',
          confirmButtonColor: mainColors.primary,
          html: `<a href=https://testnet.bscscan.com/tx/${transactionDetails?.transactionHash}
            target='_blank'> Check Detail Transaction !</a>`,
        });
      }
    } catch (err) {
      console.error('Error during contract execution:', err);
      const errorMessage = getContractExecutionErrorMessage(err);
      const transaction_hash = '';
      const transaction_status = false;
      const transaction_time = new Date();
      const project_id = 1;
      const project_name = itemName === "MERX_presale_abi" ? "MERCURX_PRESALE" : "MERCURX_STAKE";
      const token_count = paramValues.length > 1 ? paramValues[paramValues.length - 1] : 0;
      // TODO : 0lar null olacak.
      const user_public_address = signerAddress;
      const selectedAbi = getSelectedAbi();
      const action = selectedAbi?.name || selectedFunction;
      const token_address = tokenAddress;
      const payload2 = {
        action,
        project_id,
        project_name,
        token_count,
        user_public_address,
        token_address,
        transaction_hash,
        transaction_time,
        transaction_status,
      };
      console.log(payload2)
      transactionRequest(payload2);

      if (selectedAbi?.stateMutability === 'view') {
        if (contractFunctions.length === 0) {
          setResults({ error: errorMessage });
        } else {
          setResultsInput({ error: errorMessage });
        }
      }

      Swal.fire({
        icon: 'error',
        iconColor: mainColors.primary,
        title: 'Request failed',
        text: errorMessage,
        confirmButtonColor: mainColors.primary,
      });
    } finally {
      setLoadingFull({ isLoading: false });
    }
  };

  const handleParamChange = (index, value) => {
    const newValues = [...paramValues];
    newValues[index] = value;
    setParamValues(newValues);
  };

  const getDropdownColor = () => {
    if (!selectedFunction) {
      return 'grey';
    }

    const func = getSelectedAbi();
    if (isReadFunction(func)) {
      return mainColors['primary'];
    }

    return 'orange';
  };
  const handleFunctionChange = (fn) => {
    const selected = getFunctionSignature(fn);
    setSelectedFunction(selected);
    const func = fn;
    setContractFunctions(func?.inputs || []);

    setResults(null);
    setResultsInput(null);
    setParamValues([]);
    setTransactions([]);
  };

  const handleExecuteContract = () => {
    if (!validateParams()) {
      Swal.fire({
        icon: 'error',
        iconColor: mainColors['error'],
        text: 'Please correct the highlighted inputs and try again.',
        confirmButtonColor: mainColors.primary,
      });
      return;
    }

    runContract();
  };

  const { writeFunctions, readFunctions } = getGroupedAbiFunctions();

  return (
    <div className="mercurx-contract-detail-inputs m-4">
      <Row>
        <Col md={4}>
          <Form.Group controlId="selectFunction">
            <Form.Label className='mt-2'>Select Function</Form.Label>
            <Dropdown onToggle={(isOpen) => setIsDropdownOpen(isOpen)}>
              <Dropdown.Toggle
                variant="primary"
                id="dropdown-basic"
                className="contract-function-dropdown-toggle"
                disabled={!accounts?.[0]}
                style={{
                  width: '100%',
                  backgroundColor: getDropdownColor(),
                  borderColor: !selectedFunction
                    ? 'grey'
                    : isReadFunction(getSelectedAbi())
                      ? mainColors['primary']
                      : 'orange'
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="contract-function-selected text-truncate"
                    title={getSelectedAbi() ? getFunctionLabel(getSelectedAbi()) : 'Select a function'}
                  >
                    {getSelectedAbi() ? getFunctionShortLabel(getSelectedAbi()) : 'Select a function'}
                  </span>
                  <MercurXIcons
                    name={isDropdownOpen ? "BiSolidUpArrowAlt" : "BiSolidDownArrowAlt"}
                    size={20}
                  />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="contract-function-dropdown-menu">
                {writeFunctions.length > 0 && (
                  <Dropdown.Header className="contract-function-group-header">
                    Write Functions
                  </Dropdown.Header>
                )}
                {writeFunctions.map((fn, index) => (
                  <Dropdown.Item
                    key={`${getFunctionSignature(fn)}-write-${index}`}
                    onClick={() => handleFunctionChange(fn)}
                    style={{
                      backgroundColor: 'orange',
                      color: 'white',
                      borderColor: 'orange'
                    }}
                  >
                    <div className="contract-function-item-title">{fn.name}</div>
                    <div className="contract-function-item-meta">{getFunctionLabel(fn)}</div>
                  </Dropdown.Item>
                ))}
                {writeFunctions.length > 0 && readFunctions.length > 0 && <Dropdown.Divider />}
                {readFunctions.length > 0 && (
                  <Dropdown.Header className="contract-function-group-header">
                    Read Functions
                  </Dropdown.Header>
                )}
                {readFunctions.map((fn, index) => (
                  <Dropdown.Item
                    key={`${getFunctionSignature(fn)}-read-${index}`}
                    onClick={() => handleFunctionChange(fn)}
                    style={{
                      backgroundColor: mainColors['primary'],
                      color: 'white',
                      borderColor: mainColors['primary']
                    }}
                  >
                    <div className="contract-function-item-title">{fn.name}</div>
                    <div className="contract-function-item-meta">{getFunctionLabel(fn)}</div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
          <Form.Group className="mt-3" controlId="contractAddress">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <Form.Label className="mb-0">Contract Address</Form.Label>
              <Button
                variant="link"
                className="contract-address-copy-button"
                onClick={handleCopyContractAddress}
                disabled={!hasResolvedContractAddress}
                aria-label="Copy contract address"
                title={hasResolvedContractAddress ? 'Copy contract address' : 'No contract address to copy'}
              >
                <MercurXIcons
                  name="BiCopy"
                  size={18}
                  color={hasResolvedContractAddress ? mainColors.primary : '#adb5bd'}
                />
              </Button>
            </div>
            <Form.Control
              type="text"
              value={resolvedContractAddress}
              readOnly
              placeholder="No saved contract address"
              isInvalid={!hasResolvedContractAddress}
            />
            <Form.Control.Feedback type="invalid">
              No valid contract address was returned from `/contracts` for this ABI.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col className='mt-2' md={8}>
          {contractFunctions.length > 0 && (
            <>
              {contractFunctions.map((param, index) => (
                <Form.Group key={param.name} controlId={`param-${index}`}>
                  <Form.Label className='mt-2'>{param.name}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={`Enter ${param.name}`}
                    onChange={(e) => handleParamChange(index, e.target.value)}
                    isInvalid={validationAddressError && selectedFunction && validationAddressError.includes(param.name)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationAddressError && selectedFunction && validationAddressError.includes(param.name) ? validationAddressError : ''}
                  </Form.Control.Feedback>
                </Form.Group>
              ))}
            </>
          )}

          {selectedFunction && (
            <Button
              variant="primary"
              className="mt-3"
              onClick={handleExecuteContract}
              disabled={!hasResolvedContractAddress}
              style={{
                backgroundColor: getDropdownColor(),
                borderColor: getDropdownColor()
              }}
            >
              Execute
            </Button>
          )}

          {resultsInput !== null && resultsInput !== undefined && (
            renderResultCard('Input Results', resultsInput)
          )}

          {results !== null && results !== undefined && (
            renderResultCard('Results', results)
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          {transactions.length > 0 && (
            <div className="text-fs-head-sm">Transaction Details:</div>
          )}
          {transactions.map((txn, index) => (
            <Card key={index} className="mb-2 transaction-card">
              <Card.Body>
                <pre>{JSON.stringify(txn, null, 2)}</pre>
              </Card.Body>
            </Card>
          ))}
        </Col>
      </Row>
    </div>
  );

};

const mapStateToProps = (state) => ({
  abiHistory: state.abiReducer.abiHistory,
  bnbData: state.bnbReducer.bnb,
  isLoading: state.loadingReducer.isLoading,
  projects: state.projectReducer.projects,
  accounts: state.walletReducer.accounts,
});

const mapDispatchToProps = (dispatch) => ({
  setLoadingFull: (payload) => dispatch(setLoadingFullAction(payload)),
  abiHistoryRequest: (payload) => dispatch(abiRequestAction(payload)),
  getBNBRequest: (payload) => dispatch(getBNB(payload)),
  setAlert: (payload) => dispatch(setAlertAction(payload)),
  transactionRequest: (creds) => dispatch(transactionRequest(creds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MercurXContractDetailInputs);
